import {useMemo} from 'react'
import {FRAUD_DETECTION_THRESHOLDS} from '../consts/fraudDetection'
import {
  calculateStringSimilarity,
  normalizeForComparison,
  isAddressMatch,
} from '../utils/stringUtils'
import {parseOcrAddress} from '../utils/addressParser'
import {GoriSignatureResponse} from '../core/_requests'

interface OcrAddressData {
  from_street1: string
  to_street1: string
  barcode_number?: string
}

export interface FieldStatus {
  fromAddress: boolean // true = mismatch
  toAddress: boolean // true = mismatch
  tracking: boolean // true = mismatch
}

export function useFraudDetection(
  data: GoriSignatureResponse | undefined,
  ocrAddresses: OcrAddressData | null,
  isManualEntry: boolean
) {
  const parsedOcrAddresses = useMemo(() => {
    if (!ocrAddresses) return null

    return {
      from: parseOcrAddress(ocrAddresses.from_street1),
      to: parseOcrAddress(ocrAddresses.to_street1),
    }
  }, [ocrAddresses])

  const normalizedApiAddresses = useMemo(() => {
    if (!data || !data.valid) return null
    return {
      fromStreet: normalizeForComparison(data.from_street1),
      fromCity: normalizeForComparison(data.from_city),
      fromZip: data.from_zip,
      toStreet: normalizeForComparison(data.to_street1),
      toCity: normalizeForComparison(data.to_city),
      toZip: data.to_zip,
    }
  }, [data])

  const normalizedOcrAddresses = useMemo(() => {
    if (!parsedOcrAddresses) return null
    return {
      fromStreet: normalizeForComparison(parsedOcrAddresses.from.street),
      fromCity: normalizeForComparison(parsedOcrAddresses.from.city),
      fromZip: parsedOcrAddresses.from.zip,
      toStreet: normalizeForComparison(parsedOcrAddresses.to.street),
      toCity: normalizeForComparison(parsedOcrAddresses.to.city),
      toZip: parsedOcrAddresses.to.zip,
    }
  }, [parsedOcrAddresses])

  const hasAddressMismatch = useMemo(() => {
    if (
      !data ||
      !data.valid ||
      !normalizedOcrAddresses ||
      !normalizedApiAddresses ||
      isManualEntry
    ) {
      return false
    }

    // Only check TO address (FROM address validation removed)
    const toStreetMatch = isAddressMatch(
      normalizedApiAddresses.toStreet,
      normalizedOcrAddresses.toStreet,
      FRAUD_DETECTION_THRESHOLDS.ADDRESS_SIMILARITY
    )
    const toCityMatch = isAddressMatch(
      normalizedApiAddresses.toCity,
      normalizedOcrAddresses.toCity,
      FRAUD_DETECTION_THRESHOLDS.ADDRESS_SIMILARITY
    )
    const toZipMatch = isAddressMatch(
      normalizedApiAddresses.toZip,
      normalizedOcrAddresses.toZip,
      FRAUD_DETECTION_THRESHOLDS.ZIP_SIMILARITY
    )

    const toMatch = toStreetMatch && toCityMatch && toZipMatch

    return !toMatch
  }, [data, normalizedOcrAddresses, normalizedApiAddresses, isManualEntry])

  const hasLabelBarcodeMismatch = useMemo(() => {
    if (!data || !data.valid || !ocrAddresses || isManualEntry) return false

    const normalizeBarcode = (barcode?: string): string => {
      return barcode?.replace(/\s+/g, '').toLowerCase() || ''
    }

    const ocrBarcode = normalizeBarcode(ocrAddresses.barcode_number)
    const apiBarcode = normalizeBarcode(data.tracking_number)

    const similarity = calculateStringSimilarity(ocrBarcode, apiBarcode)

    return similarity < FRAUD_DETECTION_THRESHOLDS.BARCODE_SIMILARITY
  }, [data, ocrAddresses, isManualEntry])

  const fieldStatus: FieldStatus = useMemo(() => {
    if (
      !data ||
      !data.valid ||
      !normalizedOcrAddresses ||
      !normalizedApiAddresses ||
      isManualEntry
    ) {
      return {fromAddress: false, toAddress: false, tracking: false}
    }

    // FROM address validation removed - always returns false (no mismatch)
    const fromMismatch = false

    // Check TO address using substring matching
    const toStreetMatch = isAddressMatch(
      normalizedApiAddresses.toStreet,
      normalizedOcrAddresses.toStreet,
      FRAUD_DETECTION_THRESHOLDS.ADDRESS_SIMILARITY
    )
    const toCityMatch = isAddressMatch(
      normalizedApiAddresses.toCity,
      normalizedOcrAddresses.toCity,
      FRAUD_DETECTION_THRESHOLDS.ADDRESS_SIMILARITY
    )
    const toZipMatch = isAddressMatch(
      normalizedApiAddresses.toZip,
      normalizedOcrAddresses.toZip,
      FRAUD_DETECTION_THRESHOLDS.ZIP_SIMILARITY
    )

    const toMismatch = !(toStreetMatch && toCityMatch && toZipMatch)

    // Check tracking number
    const normalizeBarcode = (barcode?: string): string => {
      return barcode?.replace(/\s+/g, '').toLowerCase() || ''
    }

    const ocrBarcode = normalizeBarcode(ocrAddresses?.barcode_number)
    const apiBarcode = normalizeBarcode(data.tracking_number)
    const trackingSimilarity = calculateStringSimilarity(ocrBarcode, apiBarcode)
    const trackingMismatch = trackingSimilarity < FRAUD_DETECTION_THRESHOLDS.BARCODE_SIMILARITY

    return {
      fromAddress: fromMismatch,
      toAddress: toMismatch,
      tracking: trackingMismatch,
    }
  }, [data, normalizedOcrAddresses, normalizedApiAddresses, ocrAddresses, isManualEntry])

  return {hasAddressMismatch, hasLabelBarcodeMismatch, fieldStatus}
}
